// iOS-style frosted glass notification toast
import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { NotificationData, notificationColors } from '@/entities/Notification';

interface GlassNotificationToastProps {
  notification: NotificationData;
  onDismiss: (id: number) => void;
  onClick?: (notification: NotificationData) => void;
  index?: number; // For stacking effect
}

export function GlassNotificationToast({
  notification,
  onDismiss,
  onClick,
  index = 0,
}: GlassNotificationToastProps) {
  // Default to 'system' if type is invalid or missing
  const colors = notificationColors[notification.type] || notificationColors['system'];
  // Removed auto-dismiss - user must manually dismiss all notifications

  const handleClick = () => {
    // Only navigate if there's an actionUrl, don't dismiss
    if (onClick && notification.actionUrl) {
      onClick(notification);
    }
  };

  return (
    <div
      className="w-80 transform transition-all duration-500 ease-out animate-slide-in-down"
      style={{
        zIndex: 9999 - index,
      }}
    >
      {/* iOS Glassmorphism Card */}
      <div
        className={`
          relative overflow-hidden rounded-2xl
          border ${colors.border}
          shadow-2xl shadow-black/40
          cursor-pointer
          hover:scale-[1.02] transition-transform
        `}
        onClick={handleClick}
        style={{
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        {/* Accent bar */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${colors.accent}`} />

        {/* Content */}
        <div className="p-4 pl-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              {/* Icon + Title */}
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">{colors.icon}</span>
                <h3 className="font-semibold text-white text-xs">
                  {notification.title}
                </h3>
              </div>

              {/* Sender name (if applicable) */}
              {notification.fromName && (
                <p className="text-[10px] text-slate-300 mb-1">
                  From: {notification.fromName}
                </p>
              )}

              {/* Message */}
              <p className="text-xs text-slate-200 leading-relaxed">
                {notification.message}
              </p>

              {/* Priority badge */}
              {notification.priority === 'urgent' && (
                <span className="inline-block mt-2 px-2 py-0.5 bg-red-500/30 text-red-200 text-xs rounded-full font-medium">
                  URGENT
                </span>
              )}
            </div>

            {/* Dismiss button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (notification.id) onDismiss(notification.id);
              }}
              className="flex-shrink-0 p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-slate-300" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Add these animations to your global CSS or tailwind config
// @keyframes slide-in-right {
//   from {
//     transform: translateX(120%);
//     opacity: 0;
//   }
//   to {
//     transform: translateX(0);
//     opacity: 1;
//   }
// }
//
// @keyframes progress {
//   from {
//     width: 100%;
//   }
//   to {
//     width: 0%;
//   }
// }
